import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import { Box, Typography, Button, useMediaQuery, CircularProgress } from '@mui/material'
import { useSelector } from 'react-redux'
import classNames from 'classnames'
import { styled } from '@mui/material/styles'
import ContentGroup from '../ContentGroup'
import { handleUrlHash } from './helpers'
import { TabsWrapper, TabsHeaderWrapper, TabsHeader, TabButtons, TabPanelStyles } from './index.styles'

const TabPanel = ({ children, value, index, className }) => (
  <TabPanelStyles
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    aria-labelledby={`tab-${index}`}
    className={className}
  >
    <Box sx={{ p: 0, ...(value === index ? { display: 'block' } : { display: 'none' }) }}>
      <Typography>{children}</Typography>
    </Box>
  </TabPanelStyles>
)

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
  className: PropTypes.string,
}

const StrapiTabs = ({ data }) => {
  const [tabBoundary, setTabBoundary] = useState({})
  const [initialTabSet, setInitialTabSet] = useState(false)
  const [value, setValue] = useState(null)
  const [loading, setLoading] = useState(true)
  const tabButtonsRef = useRef(null)
  const tabsHeaderWrapperRef = useRef(null)
  const tabsScrollerRef = useRef(null)
  const buttonsCacheRef = useRef(null)
  const buttonCountCacheRef = useRef(0)
  const TabsData = data?.TabGroup?.Tabs
  const multipleTabs = TabsData?.length > 3
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'))
  const { showMeganavMobile, hideInfoBanner } = useSelector(state => state.global)
  const headerSize = useSelector(state => state.global.headerSize)
  const showToolbar =
    typeof document !== 'undefined' && !document?.getElementById('mobile-toolbar')?.classList?.contains?.('hidden')

  useLayoutEffect(() => {
    function getTabPosition() {
      const divBoundaries = tabsHeaderWrapperRef.current.getBoundingClientRect()
      const targetDiv = tabsHeaderWrapperRef.current
      if (divBoundaries.top > 0) {
        if (divBoundaries.top <= 111) {
          targetDiv.classList.add('sticky-on-top')
        } else {
          targetDiv.classList.remove('sticky-on-top')
        }
      }
    }
    window.addEventListener('scroll', getTabPosition)
    return () => window.removeEventListener('scroll', getTabPosition)
  }, [])

  const maxTabs = data?.TabGroup?.Tabs?.length

  const updateTabFromUrl = useCallback(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      const tabParam = Number(params.get('tab')) - 1
      const tabExists = tabParam < maxTabs && tabParam >= 0

      if (typeof tabParam === 'number' && tabExists) {
        setValue(tabParam)
        setInitialTabSet(true)
      } else if (window.location.hash) {
        setInitialTabSet(true)
      } else {
        setValue(0)
        window.scrollTo(0, 0)
      }
    }
  }, [maxTabs])

  useLayoutEffect(() => {
    updateTabFromUrl()
  }, [updateTabFromUrl])

  // Listen for URL changes (Gatsby navigation and browser back/forward)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleUrlChange = () => {
        updateTabFromUrl()
      }
      // Listen for Gatsby route changes
      window.addEventListener('locationchange', handleUrlChange)

      // Listen for browser back/forward navigation
      window.addEventListener('popstate', handleUrlChange)

      return () => {
        window.removeEventListener('locationchange', handleUrlChange)
        window.removeEventListener('popstate', handleUrlChange)
      }
    }
    return undefined
  }, [updateTabFromUrl])

  // Helper function to get cached buttons array
  const getButtonsArray = useCallback(() => {
    const container = tabButtonsRef.current
    if (!container) return []

    // Get current button count
    const currentButtonCount = Array.from(container.querySelectorAll('button')).length

    // Invalidate cache if button count changed
    if (!buttonsCacheRef.current || buttonCountCacheRef.current !== currentButtonCount) {
      buttonsCacheRef.current = Array.from(container.querySelectorAll('button'))
      buttonCountCacheRef.current = currentButtonCount
    }

    return buttonsCacheRef.current
  }, [])

  useLayoutEffect(() => {
    const btns = getButtonsArray()
    const selectedTab = btns[value]
    if (selectedTab) {
      setTabBoundary({ width: selectedTab.offsetWidth, left: selectedTab.offsetLeft })
    }
  }, [data, value, isMobile, getButtonsArray])

  useEffect(() => {
    if (initialTabSet && window.location.hash && typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        handleUrlHash()
      })
    }
  }, [initialTabSet, value])

  useEffect(() => {
    setLoading(true)
  }, [])

  useEffect(() => {
    setLoading(true)
    if (TabsData && value !== null && TabsData[value]?.ContentGroup) {
      setLoading(false)
    }
  }, [value, TabsData])

  // This effect takes the current tab value and updates the URL with a search param for the tab.
  // This keeps the current tab when modifying another parameter like with search query pagination.
  useEffect(() => {
    if (value !== null && typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      const { search } = url
      const params = new URLSearchParams(search)
      params.set('tab', value + 1)
      const newUrl = `${url.pathname}?${params.toString()}`
      window.history.replaceState(null, '', newUrl)
    }
  }, [value])

  useLayoutEffect(() => {
    const scroller = tabsScrollerRef.current
    if (!scroller || value == null) return

    const btns = getButtonsArray()
    const active = btns[value]
    if (!active) return

    const target =
      active.offsetLeft - // start of button
      scroller.clientWidth / 2 + // half viewport
      active.offsetWidth / 2 // half button

    const max = scroller.scrollWidth - scroller.clientWidth
    const nextLeft = Math.max(0, Math.min(target, Math.max(0, max)))

    // Smooth scroll (use rAF to ensure layout is painted)
    window.requestAnimationFrame(() => {
      scroller.scrollTo({ left: nextLeft, behavior: 'smooth' })
    })
  }, [value, isMobile, getButtonsArray])

  const recomputeIndicator = React.useCallback(() => {
    if (value == null) return
    const container = tabButtonsRef.current
    if (!container) return

    const btns = getButtonsArray()
    const active = btns[value]
    if (!active) return

    setTabBoundary({
      width: active.offsetWidth,
      left: active.offsetLeft,
    })
  }, [value, getButtonsArray])

  // Center scroller on the active button
  const centerActiveTab = React.useCallback(() => {
    if (!isMobile || !multipleTabs || value == null) return

    const scroller = tabsScrollerRef.current
    if (!scroller) return

    const btns = getButtonsArray()
    const active = btns[value]
    if (!active) return

    const target = active.offsetLeft - scroller.clientWidth / 2 + active.offsetWidth / 2

    const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth)
    const left = Math.max(0, Math.min(target, max))

    scroller.scrollTo({ left, behavior: 'auto' })
  }, [value, isMobile, multipleTabs, getButtonsArray])

  // On value change: center and recompute indicator in a single frame
  useLayoutEffect(() => {
    if (!isMobile || !multipleTabs) return
    window.requestAnimationFrame(() => {
      recomputeIndicator()
      centerActiveTab()
    })
  }, [value, centerActiveTab, recomputeIndicator, isMobile, multipleTabs])

  // After web fonts load: recenter and recompute once
  useEffect(() => {
    if ('fonts' in document && document.fonts?.ready) {
      document.fonts.ready.then(() => {
        recomputeIndicator()
        if (isMobile && multipleTabs) centerActiveTab()
      })
    }
  }, [centerActiveTab, recomputeIndicator, isMobile, multipleTabs])

  // Recompute on resize (viewport/orientation changes)
  useEffect(() => {
    const onResize = () => {
      recomputeIndicator()
      if (isMobile && multipleTabs) centerActiveTab()
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [centerActiveTab, recomputeIndicator, isMobile, multipleTabs])

  // Listening for sticky class toggles on scroll
  useEffect(() => {
    if (!isMobile || !multipleTabs) return undefined

    let scrollTimeoutId = null
    const debounceMs = 150 // Wait 150ms after scroll ends before recomputing

    const onScroll = () => {
      // Clear existing timeout
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId)
      }

      // Set timeout to debounce
      scrollTimeoutId = setTimeout(() => {
        window.requestAnimationFrame(() => {
          recomputeIndicator()
          centerActiveTab()
        })
      }, debounceMs)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (scrollTimeoutId) clearTimeout(scrollTimeoutId)
    }
  }, [centerActiveTab, recomputeIndicator, isMobile, multipleTabs])

  const handleChange = (event, newValue) => {
    setValue(newValue)
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      if (newValue === 0) {
        params.delete('tab')
      } else {
        params.set('tab', newValue + 1)
      }
      params.delete('page')
      const url = new URL(window.location.href)
      url.search = params.toString()
      window.history.replaceState({}, '', url.toString())
    }
  }

  const hideSearchQueryVisualFilter = tab => {
    if (!tab?.ContentGroup?.Content) return false
    const hasVisualFilter = tab.ContentGroup.Content.some(content => content?.__typename === 'PageVisualFilter')
    const hasSearchQueryVisualFilter = tab.ContentGroup.Content.some(
      content =>
        content?.__typename === 'PageSearchQuery' &&
        (content.SearchQuery?.VisualFilter ||
          content.SearchQuery?.FLVisualFilter ||
          content.SearchQuery?.OOMVisualFilter ||
          content.SearchQuery?.SEVisualFilter ||
          content.SearchQuery?.TXVisualFilter),
    )
    return hasVisualFilter && hasSearchQueryVisualFilter
  }

  return (
    <TabsWrapper
      data-testid={data?.Tabs?.testId}
      displayDesktop={data?.TabGroup?.DisplayDesktop}
      displayMobile={data?.TabGroup?.DisplayMobile}
    >
      <TabsHeaderWrapper
        id="tabs-header-wrapper"
        ref={tabsHeaderWrapperRef}
        showMeganavMobile={showMeganavMobile}
        showToolbar={showToolbar}
        stickyOffset={headerSize}
      >
        <TabsHeader ref={tabsScrollerRef}>
          <TabButtons
            ref={tabButtonsRef}
            className="tabs-container"
            aria-label="tabs"
            multipleTabs={multipleTabs}
            value={value}
            tabsLength={data?.TabGroup?.Tabs?.length}
          >
            {TabsData &&
              TabsData.map((tab, index) => (
                <Button
                  key={`tab-${tab.TabName}`}
                  role="tab"
                  name={tab.TabName}
                  className={classNames(`tab-button ${data?.TabGroup?.testId}-${index}`, { checked: index === value })}
                  label={tab.TabName}
                  onClick={e => handleChange(e, index)}
                  aria-selected={index === value}
                >
                  {tab.TabName}
                </Button>
              ))}
            <span className="indicator" style={{ ...tabBoundary }} />
          </TabButtons>
        </TabsHeader>
      </TabsHeaderWrapper>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            height: '100vh',
            paddingTop: '80px',
            width: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        TabsData &&
        TabsData.map((tab, index) => {
          const hideTabSearchQueryVisualFilter = hideSearchQueryVisualFilter(tab)
          return (
            <TabPanel key={`tab-panel-${tab.TabName}`} value={value} index={index}>
              {value === index && (
                <ContentGroup
                  key={value}
                  data={tab?.ContentGroup}
                  contentTab={tab.TabName !== null}
                  hideTabSearchQueryVisualFilter={hideTabSearchQueryVisualFilter}
                />
              )}
            </TabPanel>
          )
        })
      )}
    </TabsWrapper>
  )
}

export default React.memo(StrapiTabs)

StrapiTabs.propTypes = { data: PropTypes.object }

export const tabGroupFragment = graphql`
  fragment StrapiPageTabGroupFragment on StrapiTabGroup {
    id
    Title
    testId
    Tabs {
      TabName
      ContentGroup {
        ...StrapiContentGroupFragment
      }
    }
    DisplayDesktop
    DisplayMobile
  }
`
